import { Controller, useFormContext } from 'react-hook-form';
import { TextField } from '@cencosud-cencommerce/paris-uikit';

import { useAppSelector } from '@/Application/Hooks/Redux';
import { noEmojiRegexExp } from '@/constants';
import { isDeviceKiosco } from '@/modules/perseo-billing/utils';
import type { AddressDetailsProps } from '@/sections/address/components/AddressForm';
import { Autocomplete } from '@/sections/address/components/AddressForm/Autocomplete';
import {
  LABEL_ADDITIONAL_INFO,
  LABEL_ADDITIONAL_STREET_INFO,
  LABEL_ALIAS
} from '@/sections/address/components/AddressForm/constants';
import { ManualStreetAddress } from '@/sections/address/components/AddressForm/ManualStreetAddress';
import { ToggleAutocomplete } from '@/sections/address/components/AddressForm/ToggleAutocomplete';
import { getInputStatus } from '@/sections/address/components/AddressForm/utils';
import type { AddressFormValues } from '@/sections/address/types/AddressFormValues';

export const AddressDetails = (props: AddressDetailsProps) => {
  const cart = useAppSelector((state) => state.cart.data);
  const isKiosco = isDeviceKiosco();
  const { control, watch } = useFormContext<AddressFormValues>();
  const isAutocomplete = watch('isAutocomplete');

  const hasRegion = !!watch('region');
  const hasCommune = !!watch('commune');
  const hasStreetName = !!watch('streetName');

  const showAdditionalStreetFields = hasRegion && hasCommune && hasStreetName;

  const isGuest = !!cart?.anonymousId;

  return (
    <>
      <ToggleAutocomplete />

      {isAutocomplete ? <Autocomplete /> : <ManualStreetAddress {...props} />}

      {showAdditionalStreetFields && (
        <>
          <Controller
            control={control}
            name="additionalStreetInfo"
            render={({ field, fieldState, formState }) => (
              <TextField
                {...field}
                label={LABEL_ADDITIONAL_STREET_INFO}
                helperText={fieldState.error?.message}
                maxLength={75}
                status={getInputStatus(formState, fieldState)}
                onChange={(e) => {
                  const value = e.target.value.replace(noEmojiRegexExp, '');
                  field.onChange(value);
                }}
              />
            )}
          />

          {!isKiosco && !isGuest && (
            <Controller
              control={control}
              name="alias"
              render={({ field, fieldState, formState }) => (
                <TextField
                  {...field}
                  label={LABEL_ALIAS}
                  helperText={fieldState.error?.message}
                  maxLength={250}
                  status={getInputStatus(formState, fieldState)}
                  onChange={(e) => {
                    const value = e.target.value.replace(noEmojiRegexExp, '');
                    field.onChange(value);
                  }}
                />
              )}
            />
          )}

          <Controller
            control={control}
            name="additionalAddressInfo"
            render={({ field, fieldState, formState }) => (
              <TextField
                {...field}
                maxLength={250}
                label={LABEL_ADDITIONAL_INFO}
                helperText={fieldState.error?.message}
                status={getInputStatus(formState, fieldState)}
                onChange={(e) => {
                  const value = e.target.value.replace(noEmojiRegexExp, '');
                  field.onChange(value);
                }}
              />
            )}
          />
        </>
      )}
    </>
  );
};

AddressDetails.displayName = 'AddressDetails';

export default AddressDetails;
